import { CustomerShipmentDetails } from "./customer-shipment-details";
import { useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { Calendar } from "lucide-react";
import { ShipmentCustomerActions } from "./components/shipment-customer-actions";
import { Link } from "react-router";
import {
  getOrderStatus,
  isOrderAwaitingPayment,
} from "@/api/use-customer-orders-v2";
import { ShipmentStatusBadge } from "@/components/shipment-status-badge";
import type { Order } from "@/api.types.v2";
import { useRequestCancellation } from "@/api/use-request-cancellation";
import { toast } from "@/hooks/use-toast";
import { PayOrderDialog } from "./pay-order-dialog";
import { CancellationPendingNotice } from "./cancellation-pending-notice";
import { useTranslation } from "react-i18next";
import { canRequestCustomerShipmentCancellation } from "@/api/shipment-action-eligibility";

import {
  OrderPaymentSummary,
  OrderPriceDetails,
} from "./order-payment-summary";

function ProofPhotoPreview({
  photo,
  alt,
}: {
  photo: { url: string; thumbnailUrl?: string | null };
  alt: string;
}) {
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  return (
    <img
      src={
        !thumbnailFailed && photo.thumbnailUrl ? photo.thumbnailUrl : photo.url
      }
      alt={alt}
      onError={() => setThumbnailFailed(true)}
      className="w-20 h-20 object-cover rounded-md border border-default-200"
    />
  );
}

interface OrderCardDetailedProps {
  order: Order;
}

export function OrderCardDetailed({ order }: OrderCardDetailedProps) {
  const { t, i18n } = useTranslation("app");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const status = getOrderStatus(order);
  const shipment = order.shipments?.[0];
  const requestCancellation = useRequestCancellation({
    onSuccess: (data) => {
      toast({
        title: t("orders.toastCancellationTitle"),
        description: data.message,
      });
      setCancelDialogOpen(false);
    },
    onError: () =>
      toast({
        variant: "destructive",
        title: t("orders.toastCancellationError"),
      }),
  });

  const hasPendingCancellation = order.shipments?.some(
    (s) => s.cancellationRequestedAt
  );

  const showPay = isOrderAwaitingPayment(order);
  const cancellationShipments =
    order.shipments?.filter((item) =>
      canRequestCustomerShipmentCancellation(item.status)
    ) ?? [];
  const orderReference = `ORD-${order.orderNumber}`;
  const showCancel =
    cancellationShipments.length > 0 && !hasPendingCancellation;
  const handleCancelConfirm = () => {
    requestCancellation.mutate({ orderId: order.id });
  };

  return (
    <div className="space-y-6">
      {hasPendingCancellation && <CancellationPendingNotice />}
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3 rounded-xl border border-default-200 bg-default-50/40 p-4">
        <div>
          <Link
            to={`/orders/${order.id}`}
            className="text-xl font-bold font-mono text-default-900 hover:text-primary"
          >
            SHP-{shipment?.shipmentNumber ?? "—"}
          </Link>
          <div className="flex items-center gap-1.5 mt-1 text-xs text-default-500">
            <Calendar className="h-3.5 w-3.5" />
            {new Date(order.createdAt).toLocaleDateString(
              i18n.language === "zh" ? "zh-CN" : "en-CA",
              {
                month: "short",
                day: "numeric",
                year: "numeric",
              }
            )}
          </div>
        </div>
        <div className="min-w-0">
          <OrderPaymentSummary order={order} variant="detail" />
        </div>
        <div className="col-span-2 flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {status && (
              <ShipmentStatusBadge
                status={status}
                paymentStatus={order.paymentStatus}
                className="px-4! py-1.5! text-sm!"
              />
            )}
          </div>
          <OrderPriceDetails order={order} />
        </div>
        <div className="col-span-2 flex min-w-0 flex-wrap items-center gap-2 border-t border-default-200 pt-3">
          {showPay && (
            <Button
              size="sm"
              color="primary"
              variant="flat"
              onPress={() => setPayDialogOpen(true)}
            >
              {t("orders.pay")}
            </Button>
          )}
          {shipment && (order.shipments?.length ?? 0) === 1 && (
            <ShipmentCustomerActions
              shipment={shipment}
              disabled={Boolean(hasPendingCancellation)}
            />
          )}
          {showCancel && (
            <Button
              size="sm"
              color="danger"
              variant="bordered"
              className="max-w-full whitespace-normal sm:ml-auto"
              aria-label={t("orderDetails.requestOrderCancellation", {
                orderReference,
              })}
              onPress={() => setCancelDialogOpen(true)}
            >
              {t("orders.requestCancellation")}
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-5">
        {order.shipments?.map((item) => (
          <CustomerShipmentDetails
            key={item.id}
            shipment={item}
            showHeading={(order.shipments?.length ?? 0) > 1}
            actions={
              (order.shipments?.length ?? 0) > 1 ? (
                <ShipmentCustomerActions
                  shipment={item}
                  disabled={Boolean(hasPendingCancellation)}
                />
              ) : undefined
            }
          />
        ))}

        {(() => {
          const proofPhotos =
            shipment?.tasks
              ?.flatMap((t) => t.proof?.mediaItems ?? [])
              .filter((m) => m.url) ?? [];
          if (proofPhotos.length === 0) return null;
          return (
            <div>
              <div className="text-tiny font-semibold uppercase tracking-wider text-default-500 mb-2">
                {t("orders.deliveryPhotos", { count: proofPhotos.length })}
              </div>
              <div className="flex flex-wrap gap-2">
                {proofPhotos.map((photo) => (
                  <a
                    key={photo.id}
                    href={photo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ProofPhotoPreview
                      photo={photo}
                      alt={t("orders.deliveryProofAlt")}
                    />
                  </a>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      <PayOrderDialog
        open={payDialogOpen}
        onOpenChange={setPayDialogOpen}
        orderId={order.id}
        totalCents={order.totalCents ?? 0}
        orderNumber={shipment?.shipmentNumber?.toString()}
      />

      <Modal
        isOpen={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        placement="center"
        backdrop="blur"
        disableAnimation
        size="md"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>
                {t("orderDetails.cancellationTitle", { orderReference })}
              </ModalHeader>
              <ModalBody>
                <p className="text-sm text-default-600">
                  {t("orderDetails.cancellationBody", { orderReference })}
                </p>
                <ul className="list-disc pl-5 text-sm">
                  {cancellationShipments.map((item) => (
                    <li key={item.id}>SHP-{item.shipmentNumber ?? "…"}</li>
                  ))}
                </ul>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>
                  {t("orders.keepOrder")}
                </Button>
                <Button
                  color="danger"
                  onPress={handleCancelConfirm}
                  isDisabled={requestCancellation.isPending}
                  isLoading={requestCancellation.isPending}
                >
                  {requestCancellation.isPending
                    ? t("orders.submitting")
                    : t("orders.requestCancellation")}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
